import React, { Component } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import { fetchAll, createQuote, updateQuote, deleteQuote } from '../utils/quoteOfTheDay';

const rowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  borderBottom: '1px solid #eee',
  padding: '6px 0px',
};
const textStyle = { flex: '1 1 auto', fontSize: '13px', paddingRight: '8px', minWidth: '0px' };
const authorStyle = { fontSize: '12px', color: '#666', fontStyle: 'italic' };
const errorStyle = { fontSize: '12px', color: '#c00', margin: '4px 0px' };
const smallActionButtonStyle = { minWidth: '0px', padding: '2px 6px', fontSize: '11px' };

export class QuoteManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      quotes: [],
      draftText: '',
      draftAuthor: '',
      error: '',
      loading: false,
      // Editing (FIX 4) and delete confirmation (FIX 6) are both single-row
      // at a time - editingId/confirmDeleteId hold the one row in that mode,
      // rather than per-row state, since only one row can be mid-edit or
      // mid-delete-confirmation at once.
      editingId: null,
      editText: '',
      editAuthor: '',
      confirmDeleteId: null,
    };
    // Held on the instance so the tests can swap the calls out. There is no
    // enzyme here and no module mocking in this suite.
    this.api = { fetchAll, createQuote, updateQuote, deleteQuote };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ loading: true });
    return this.api.fetchAll().then((rows) => {
      if (!rows) {
        // List is admin-only and answers 403 to anyone else, which readJson
        // hands back as null.
        this.setState({ quotes: [], error: 'Could not load the quotes.', loading: false });
        return;
      }
      this.setState({ quotes: rows, error: '', loading: false });
    });
  };

  // readJson deliberately lets a 400 through to .json(), because several
  // endpoints answer BadRequest with a JSON body the caller is meant to
  // read. A truthy body is therefore not proof of success - Create/Update
  // answer a validation failure with { text: "..." } or { author: "..." },
  // and Update/Delete answer "no such row" with { id: "..." }. That last one
  // is the trap: a not-found error carries a truthy string under the same
  // "id" key a real saved row uses, so a bare truthy-.id check takes an
  // error for a success. Check a field only a real QuoteOfTheDay carries and
  // no error body does - isActive is always a boolean on a saved row and
  // never present on any of the error shapes above.
  isSavedQuote = (candidate) => !!(candidate && typeof candidate.isActive === 'boolean');

  isDeleteConfirmation = (candidate) => !!(candidate && candidate.deleted === true);

  messageFromBody = (body) => {
    if (body && typeof body.text === 'string') {
      return body.text;
    }
    if (body && typeof body.author === 'string') {
      return body.author;
    }
    if (body && typeof body.id === 'string') {
      return body.id;
    }
    return null;
  };

  saveDraft = () => {
    const text = (this.state.draftText || '').trim();
    if (text === '') {
      this.setState({ error: 'A quote needs some text.' });
      return Promise.resolve();
    }

    return this.api.createQuote({
      text: text,
      author: (this.state.draftAuthor || '').trim(),
      isActive: true,
    }).then((saved) => {
      if (!this.isSavedQuote(saved)) {
        // The draft stays in the box - retyping it would be the worst
        // possible answer to a failed save. Show the server's own
        // validation message when it sent one.
        this.setState({
          error: this.messageFromBody(saved) || 'Could not save that. Check your connection and try again.',
        });
        return;
      }
      this.setState({ draftText: '', draftAuthor: '', error: '' });
      return this.load();
    });
  };

  toggleActive = (quote) => () =>
    this.api.updateQuote({
      id: quote.id,
      text: quote.text,
      author: quote.author,
      isActive: !quote.isActive,
    }).then((saved) => {
      if (!this.isSavedQuote(saved)) {
        this.setState({ error: this.messageFromBody(saved) || 'Could not save that.' });
        return;
      }
      return this.load();
    });

  startEdit = (quote) => () =>
    this.setState({
      editingId: quote.id,
      editText: quote.text,
      editAuthor: quote.author || '',
      confirmDeleteId: null,
      error: '',
    });

  cancelEdit = () => this.setState({ editingId: null, editText: '', editAuthor: '' });

  handleEditField = (field) => (event) => {
    const patch = {};
    patch[field] = event.target.value;
    this.setState(patch);
  };

  saveEdit = (quote) => () => {
    const text = (this.state.editText || '').trim();
    if (text === '') {
      this.setState({ error: 'A quote needs some text.' });
      return Promise.resolve();
    }

    return this.api.updateQuote({
      id: quote.id,
      text: text,
      author: (this.state.editAuthor || '').trim(),
      isActive: quote.isActive,
    }).then((saved) => {
      if (!this.isSavedQuote(saved)) {
        // Keep the edit on screen, for the same reason a refused saveDraft
        // keeps its draft - losing the user's retype is the worst outcome.
        this.setState({ error: this.messageFromBody(saved) || 'Could not save that.' });
        return;
      }
      this.setState({ editingId: null, editText: '', editAuthor: '', error: '' });
      return this.load();
    });
  };

  // Delete is a one-click-permanent action next to an active Switch in a
  // dense row, so it takes two clicks: the first arms confirmDeleteId and
  // swaps that row's icon button for an explicit Confirm/Cancel pair; only
  // the second click actually calls the API.
  confirmDelete = (quote) => () => this.setState({ confirmDeleteId: quote.id, editingId: null });

  cancelDelete = () => this.setState({ confirmDeleteId: null });

  remove = (quote) => () =>
    this.api.deleteQuote(quote.id).then((result) => {
      if (!this.isDeleteConfirmation(result)) {
        this.setState({
          error: this.messageFromBody(result) || 'Could not delete that.',
          confirmDeleteId: null,
        });
        return;
      }
      this.setState({ confirmDeleteId: null });
      return this.load();
    });

  handleDraft = (field) => (event) => {
    const patch = {};
    patch[field] = event.target.value;
    this.setState(patch);
  };

  renderRow = (quote) => {
    if (this.state.editingId === quote.id) {
      return (
        <div key={quote.id} style={rowStyle}>
          <div style={textStyle}>
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={this.state.editText}
              onChange={this.handleEditField('editText')}
              inputProps={{ maxLength: 500, style: { fontSize: '13px' } }} />
            <TextField
              label="Author"
              value={this.state.editAuthor}
              onChange={this.handleEditField('editAuthor')}
              inputProps={{ maxLength: 120, style: { fontSize: '12px' } }}
              style={{ marginTop: '4px', width: '160px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <Button size="small" color="primary" onClick={this.saveEdit(quote)}>Save</Button>
            <Button size="small" onClick={this.cancelEdit}>Cancel</Button>
          </div>
        </div>
      );
    }

    return (
      <div key={quote.id} style={rowStyle}>
        <div style={textStyle}>
          <div>{quote.text}</div>
          <div style={authorStyle}>{quote.author || 'Unknown'}</div>
        </div>
        <IconButton title="Edit" onClick={this.startEdit(quote)} style={{ padding: '6px' }}>
          <EditIcon style={{ fontSize: '18px' }} />
        </IconButton>
        <Switch
          checked={quote.isActive}
          onChange={this.toggleActive(quote)}
          size="small"
          color="primary" />
        {this.state.confirmDeleteId === quote.id ? (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              size="small"
              color="secondary"
              onClick={this.remove(quote)}
              style={smallActionButtonStyle}>
              Confirm
            </Button>
            <Button size="small" onClick={this.cancelDelete} style={smallActionButtonStyle}>
              Cancel
            </Button>
          </span>
        ) : (
          <IconButton title="Delete" onClick={this.confirmDelete(quote)} style={{ padding: '6px' }}>
            <DeleteIcon style={{ fontSize: '18px' }} />
          </IconButton>
        )}
      </div>
    );
  };

  render() {
    return (
      <div style={{ padding: '10px', fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}>
        <h3 style={{ fontSize: '15px', margin: '0px 0px 8px 0px' }}>Quote of the Day</h3>
        <p style={{ fontSize: '12px', color: '#666', margin: '0px 0px 10px 0px' }}>
          The bulletin shows one of these each day, the same one for everybody.
          Turning a quote off leaves it here but takes it out of the rotation.
        </p>

        <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '10px' }}>
          <TextField
            label="Quote"
            multiline
            minRows={2}
            fullWidth
            value={this.state.draftText}
            onChange={this.handleDraft('draftText')}
            inputProps={{ maxLength: 500, style: { fontSize: '13px' } }} />
          <TextField
            label="Author"
            value={this.state.draftAuthor}
            onChange={this.handleDraft('draftAuthor')}
            inputProps={{ maxLength: 120, style: { fontSize: '13px' } }}
            style={{ marginLeft: '8px', width: '160px' }} />
          <Button color="primary" onClick={this.saveDraft} style={{ marginLeft: '8px' }}>
            Add
          </Button>
        </div>

        {this.state.error ? <p style={errorStyle}>{this.state.error}</p> : null}

        {this.state.quotes.map(this.renderRow)}
      </div>
    );
  }
}

export default QuoteManager;



// WEBPACK FOOTER //
// ./src/components/QuoteManager.js